import { PaletteColor } from "@mui/material";
import { Theme } from "@mui/material/styles";


export const segmentColors = [
  "#ff6c6c",
  "#3dd56d",
  "#9a5dff",
  "#ffbd45",
  "#3d9df3",
  "#fff835",
  "#20e7c5",
  "#24d4ff",
];

export const mindsetSegmentColors = [
  "#82E1F2",
  "#70c9f0",
  "#72b0e7",
  "#8294d5",
  "#9476b8",
  "#9e5892",
  "#9c3c65"
]

export const mindsetSegmentPalette = Object.fromEntries(
  mindsetSegmentColors.map((c, i) => [`segment${i}`, { main: c }])
) as Record<`segment${number}`, { main: string }>;

type SegmentKey = `segment${number}`;

export const getSegmentColor = (i: number): SegmentKey =>
  `segment${i % mindsetSegmentColors.length}` as SegmentKey;

export const getSegmentPaletteColor = (theme: Theme, i: number, total: number) => {
  const palette = theme.palette.segments;
  const n = palette.length;

  if (total <= 1) return palette[0];        // trivial case
  if (total >= n) return palette[i % n];   // more segments than colors → wrap around

  // Map i (0…total-1) → index in palette (0…n-1)
  const mapped = Math.round((i * (n - 1)) / (total - 1));
  return palette[mapped];
}

export const activationStatusColorMap: { [key: string]: string } = {
  submitted: "#cad4ec",
  "in process": "#b3edf7",
  ready: "#82e1f2",
  populated: "#9fd6b2",
  complete: "textSecondary",
};

export const tagTypeColorMap = {
  Causes: "#82e1f2",
  "Spend Merchants": "#F5B49F",
  "Spend Categories": "#ab9ec9",
  Demographics: "#fa8996",
  Interests: "#9fd6b2",
};

export const headerBackground = (palette: PaletteColor) => {
  return `linear-gradient(137deg, ${palette.main}, ${palette.dark})`;
};

export function interpolatePalette(
  palette: PaletteColor,
  percent: number,
): string {
  // Convert the hex colors to RGB values
  const r1 = parseInt(palette.main.substring(1, 3), 16);
  const g1 = parseInt(palette.main.substring(3, 5), 16);
  const b1 = parseInt(palette.main.substring(5, 7), 16);

  const r2 = parseInt(palette.dark.substring(1, 3), 16);
  const g2 = parseInt(palette.dark.substring(3, 5), 16);
  const b2 = parseInt(palette.dark.substring(5, 7), 16);

  // Interpolate the RGB values
  const r = Math.round(r1 + (r2 - r1) * percent);
  const g = Math.round(g1 + (g2 - g1) * percent);
  const b = Math.round(b1 + (b2 - b1) * percent);

  // Convert the interpolated RGB values back to a hex color
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function interpolate(
  color1: string,
  color2: string,
  percent: number,
): string {
  // Convert the hex colors to RGB values
  const r1 = parseInt(color1.substring(1, 3), 16);
  const g1 = parseInt(color1.substring(3, 5), 16);
  const b1 = parseInt(color1.substring(5, 7), 16);

  const r2 = parseInt(color2.substring(1, 3), 16);
  const g2 = parseInt(color2.substring(3, 5), 16);
  const b2 = parseInt(color2.substring(5, 7), 16);

  // Interpolate the RGB values
  const r = Math.round(r1 + (r2 - r1) * percent);
  const g = Math.round(g1 + (g2 - g1) * percent);
  const b = Math.round(b1 + (b2 - b1) * percent);

  // Convert the interpolated RGB values back to a hex color
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function interpolateDivergant(
  low: string,
  mid: string,
  high: string,
  percent: number,
  min = 0.25,
  max = 0.75,
): string {
  // Clamp the percentage to min/max range
  const p = Math.max(min, Math.min(max, percent));

  // Normalise the percent to [0,1] between min and max
  const normalised = (p - min) / (max - min);

  if (normalised < 0.5) {
    return interpolate(low, mid, normalised * 2);
  } else {
    return interpolate(mid, high, (normalised - 0.5) * 2);
  }
}

export const whiteOverlay = (v: number = 0.051) => {
  return `linear-gradient(rgba(255, 255, 255, ${v}), rgba(255, 255, 255, ${v}))`
}
